import { FormEvent, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { MainProfileLayout } from './MainProfileLayout';
import { useAuthStore } from '../../stores/authStore';
import { requestEmailVerification, updateUser } from '../../services/user.service';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ProfilePage = () => {
  const { user, setMe } = useAuthStore();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setErrorMessage('');
    setSuccessMessage('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    let valid = true;

    if (!trimmedName) {
      setNameError('* Required');
      valid = false;
    } else {
      setNameError('');
    }

    if (!trimmedEmail) {
      setEmailError('* Required');
      valid = false;
    } else if (!emailPattern.test(trimmedEmail)) {
      setEmailError('* Invalid e-mail');
      valid = false;
    } else {
      setEmailError('');
    }

    if (!valid) {
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateUser(trimmedName, trimmedEmail, password.trim() || null);
      if (response?.user) {
        await setMe(response.user);
      }
      setSuccessMessage('Your profile was successfully updated');
      setPassword('');
    } catch {
      setErrorMessage('An error occurred updating your profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainProfileLayout section="profile" headerLabel="Profile">
      <Box component="form" onSubmit={onSubmit} sx={{ maxWidth: 680 }}>
        <Stack spacing={2.5}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
          {user?.emailVerified === false && (
            <Alert severity="warning" action={<Button size="small" onClick={() => {
              void requestEmailVerification()
                .then(() => setSuccessMessage('Verification email sent. Check your inbox.'))
                .catch((error) => setErrorMessage(error instanceof Error ? error.message : 'Unable to send verification email.'));
            }}>Resend email</Button>}>
              Verify your email before you can share a board, wall or game.
            </Alert>
          )}

          <TextField
            label={nameError ? `Name ${nameError}` : 'Name'}
            error={Boolean(nameError)}
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />

          <TextField
            label={emailError ? `E-mail ${emailError}` : 'E-mail'}
            error={Boolean(emailError)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
          />

          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={isSaving}>
              {isSaving ? 'Updating...' : 'Update'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </MainProfileLayout>
  );
};
